import { DEFAULT_EMAIL_COLLECTION_POPUP, DEFAULT_POST_SUBMIT_POPUP } from '../helpers/constants';
import { FeatureType, PopUpCustomization, PopUpTrigger, SettingStructure } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { delay, isDefined } from '../helpers/utils';

import { StateController } from '../helpers/state/state-management/state-controller';
import { detectMobileViewport } from '../helpers/device';
import topicData from '../helpers/state/topic-data';

@customElement('edit-pop-up-component')
export class EditPopUpComponent extends LitElement {
    static amplitude_callsite: string = 'Edit Pop Up Component';

    @query('#email-collection')
    emailCollectionElement?: HTMLInputElement;

    @query('#point-email-collection')
    pointEmailCollectionElement?: HTMLInputElement;

    @query('#colorPickerPopUpText')
    private readonly colorPickerPopUpTextElement!: HTMLInputElement;

    @query('#colorPickerPopUpBg')
    private readonly colorPickerPopUpBgElement!: HTMLInputElement;

    @query('#colorPickerCTAText')
    private readonly colorPickerCTATextElement!: HTMLInputElement;

    @query('#colorPickerCTABg')
    private readonly colorPickerCTABgElement!: HTMLInputElement;

    // send from parent
    @property ({ type: String })
    pop_up_type: PopUpTrigger = PopUpTrigger.LogicBased;

    // send from parent
    @state()
    pop_up_id?: string;

    // need to send from parent
    @state()
    feature_type: FeatureType = FeatureType.EmailCapture;

    // need to send from parent
    @state()
    hasEntitlement: boolean = false;

    @state()
    pop_up_object?: PopUpCustomization;

    @state()
    popUpSettings: SettingStructure[] = [];

    private readonly bindTopicState = new StateController(this, topicData);

    async firstUpdated() {
        await this.startup();
    }

    updated(changedProperties: any) {
        changedProperties.forEach(async (oldValue: any, propName: any) => {
            if (propName === 'pop_up_id') {
                await this.startup();
            }
        });
    }

    async startup() {
        if (this.pop_up_id) {
            this.pop_up_object = topicData.topic?.pop_ups?.find((popUp) => popUp._id?.$oid === this.pop_up_id);
        } else if (this.hasEntitlement && topicData.isReady) {
            this.pop_up_object = this.pop_up_type === PopUpTrigger.PostSubmission ? DEFAULT_POST_SUBMIT_POPUP : DEFAULT_EMAIL_COLLECTION_POPUP;
            this.pop_up_object.organization_id = topicData.topic?.organization_id;
            this.pop_up_object.entity_ids = topicData.topic ? [topicData.topic._id ] : [];
            await this.createNewPopUp(this.pop_up_object);
        }

        this.popUpSettings = [
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'is_active',
                title: 'Pop Up Is Live',
                initialValueBoolean: this.pop_up_object?.is_active ?? false,
                flippedLanguage: false,
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
                subfields: [],
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'pop_up_header',
                title: 'Pop Up Header',
                description: 'This text will be displayed at the top of the pop up.',
                textCharLimit: 100,
                initialValueString: this.pop_up_object?.pop_up_header ?? 'Thanks for visiting!',
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
                isHidden: false,
            },
            {
                fieldType: 'string',
                inputType: 'inputLong',
                fieldName: 'pop_up_description',
                title: 'Pop Up Description',
                description: 'This text will be displayed below the header.',
                textCharLimit: 500,
                initialValueString: this.pop_up_object?.pop_up_description ?? 'Enter your email to receive updates.',
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
                isHidden: false,
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'collect_email',
                title: `Collect User's Email`,
                initialValueBoolean: this.pop_up_object?.collect_email ?? true,
                flippedLanguage: false,
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
                subfields: ['opt_in_language'],
            },
            {
                fieldType: 'string',
                inputType: 'inputLong',
                fieldName: 'opt_in_language',
                title: 'Opt In Language for Email',
                description: 'This text will be displayed next to the email collection field.',
                textCharLimit: 200,
                initialValueString: this.pop_up_object?.opt_in_language ?? 'I agree to receive updates from this map.',
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
                isHidden: true,
                isSubfield: true,
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'click_out_button',
                title: `Add Click Out Button`,
                description: 'If enabled, the pop up will display a button that will take the user to a different page.',
                initialValueBoolean: isDefined(this.pop_up_object?.cta_link) ?? false,
                flippedLanguage: false,
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
                subfields: ['cta_link', 'cta_button_text'],
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'cta_link',
                title: 'Click Out Button Link',
                description: 'This is the link that the user will be taken to when they click the button.',
                textCharLimit: 100,
                initialValueString: this.pop_up_object?.cta_link ?? '',
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
                isHidden: true,
                isSubfield: true,
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'cta_button_text',
                title: 'Link Button Text',
                description: 'This text will be displayed on the button that opens the link.',
                textCharLimit: 50,
                initialValueString: this.pop_up_object?.cta_button_text ?? 'Learn More',
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
                isHidden: true,
                isSubfield: true,
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'display_when_embeded',
                title: 'Display When Embedded',
                description: 'If enabled, the pop up will display when the map is viewed as a page and when embedded. If disabled, the pop up will only display when the map is viewed as its own page.',
                initialValueBoolean: this.pop_up_object?.display_when_embeded ?? false,
                flippedLanguage: false,
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
                isHidden: false,
            },
            {
                fieldType: 'number',
                inputType: 'inputShort',
                fieldName: 'display_number',
                title: 'Display After Number of Seconds',
                description: 'The number of seconds to wait before displaying the pop up. Leave at 0 to display immediately.',
                initialValueNumber: this.pop_up_object?.display_number ?? 0,
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
                isHidden: false,
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'display_once',
                title: 'Display More Than Once',
                description: 'If enabled, the pop up will display every time the user visits the map. If disabled, the pop up will only display once per user.',
                initialValueBoolean: this.pop_up_object?.display_once ?? false,
                flippedLanguage: true,
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
                isHidden: false,
            },
            {
                fieldType: 'string',
                inputType: 'color',
                fieldName: 'background_color',
                title: 'Background Color',
                description: 'The background color of the pop up',
                initialValueString: this.pop_up_object?.background_color ?? '#FFFFFF',
                isTopic: false,
                isSubfield: false,
                isHidden: false,
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
            },
            {
                fieldType: 'string',
                inputType: 'color',
                fieldName: 'text_color',
                title: 'Text Color',
                description: 'The color of the text in the pop up',
                initialValueString: this.pop_up_object?.text_color ?? '#000000',
                isTopic: false,
                isSubfield: false,
                isHidden: false,
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
            },
            {
                fieldType: 'string',
                inputType: 'color',
                fieldName: 'cta_text_color',
                title: 'Button Text Color',
                description: 'The color of the text on the button',
                initialValueString: this.pop_up_object?.cta_text_color ?? '#FFFFFF',
                isTopic: false,
                isSubfield: false,
                isHidden: false,
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
            },
            {
                fieldType: 'string',
                inputType: 'color',
                fieldName: 'cta_background_color',
                title: 'Button Color',
                description: 'The color of the button',
                initialValueString: this.pop_up_object?.cta_background_color ?? '#000000',
                isTopic: false,
                isSubfield: false,
                isHidden: false,
                hasEntitlement: this.hasEntitlement,
                featureType: this.feature_type,
            },
        ];
    }

    async createNewPopUp(object: PopUpCustomization) {
        if (topicData.topic) {
            const updatedPopUps = topicData.topic?.pop_ups ?? [];
            updatedPopUps.push(object);
            const updated_topic = await topicData.fetchAndUpdateTopicData({ pop_ups: updatedPopUps }, EditPopUpComponent.amplitude_callsite);
            this.pop_up_id = updated_topic._id?.$oid;
            // get last pop up
            this.pop_up_object = updated_topic.pop_ups?.[updated_topic.pop_ups.length - 1];
        }
    }

    private async hasChange(e: CustomEvent) {
        const field = e.detail.field;
        const updatedValue = e.detail.updatedValue;
        if (field === 'click_out_button') {
            if (updatedValue === false) {
                await this.makeChange({ cta_link: undefined, cta_button_text: '' });
            }
        }
        if (!isDefined(this.pop_up_object) || !this.pop_up_id) {
            return;
        }
        await this.makeChange({ [field]: updatedValue });
    }

    private async makeChange(request: Partial<PopUpCustomization>) {
        if (!isDefined(this.pop_up_object) || !this.pop_up_id) {
            return;
        }

        const updated = {
            ...this.pop_up_object,
            ...request,
        };

        const index = topicData.topic?.pop_ups?.findIndex((popUp) => popUp._id?.$oid === this.pop_up_id);
        if (index === undefined) {
            // create new pop up
            if (topicData.topic && !isDefined(topicData.topic?.pop_ups)) {
                topicData.topic.pop_ups = [updated];
                const updated_topic = await topicData.fetchAndUpdateTopicData({ pop_ups: [updated] }, EditPopUpComponent.amplitude_callsite);
                this.pop_up_id = updated_topic._id?.$oid;
                this.pop_up_object = updated_topic.pop_ups?.find((popUp) => popUp._id?.$oid === this.pop_up_id);
            } else {
                const updatedPopUps = topicData.topic?.pop_ups ?? [];
                updatedPopUps.push(updated);
                const updated_topic = await topicData.fetchAndUpdateTopicData({ pop_ups: updatedPopUps }, EditPopUpComponent.amplitude_callsite);
                this.pop_up_id = updated_topic._id?.$oid;
                this.pop_up_object = updated_topic.pop_ups?.find((popUp) => popUp._id?.$oid === this.pop_up_id);
            }
        } else {
            // update existing pop up
            const updatedPopUps = topicData.topic?.pop_ups ?? [];
            updatedPopUps[index] = updated;
            const updated_topic = await topicData.fetchAndUpdateTopicData({ pop_ups: updatedPopUps }, EditPopUpComponent.amplitude_callsite);
            this.pop_up_object = updated_topic.pop_ups?.find((popUp) => popUp._id?.$oid === this.pop_up_id);
        }
        this.requestUpdate();
    }

    private async preview() {
        // dispatch event to preview pop up
        this.dispatchEvent(
            new CustomEvent('preview-pop-up', {
                detail: {
                    popUp: this.pop_up_object,
                },
            })
        );
    }

    openUpgradeDrawerEmailCollection() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: this.feature_type
                },
            })
        );
    }

    openColorPicker(element: HTMLInputElement) {
        element.focus();

        switch (element.id) {
            case 'colorPickerPopUpText':
                element.value = this.pop_up_object?.text_color ?? DEFAULT_EMAIL_COLLECTION_POPUP.text_color;
                break;
            case 'colorPickerPopUpBg':
                element.value = this.pop_up_object?.background_color ?? DEFAULT_EMAIL_COLLECTION_POPUP.background_color;
                break;
            case 'colorPickerCTAText':
                element.value = this.pop_up_object?.cta_text_color ?? DEFAULT_EMAIL_COLLECTION_POPUP.cta_text_color;
                break;
            case 'colorPickerCTABg':
                element.value = this.pop_up_object?.cta_background_color ?? DEFAULT_EMAIL_COLLECTION_POPUP.cta_background_color;
                break;
        }

        element.click();
    }

    private toggleSubfield(e: CustomEvent, settingList: SettingStructure[]) {
        void delay(400);
        // find title in settingsList as either fieldnametoggle or fieldname textand set isHidden to false if it is true and true if it is false
        const fieldTitle = e.detail.fieldTitle;
        const setting = settingList.find((setting) => setting.fieldName === fieldTitle);

        if (isDefined(setting)) {
            if (e.detail.hidden === false) {
                setting.isHidden = false;
            } else {
            setting.isHidden = !(setting.isHidden ?? false);
            }
        }
        // update the new isHidden value in this.settingsList
        settingList = [...settingList];
        this.requestUpdate();
    }

    async onChangeIconColor(iconColor: string, type: string) {
        switch (type) {
            case 'popUpText':
                await this.makeChange({ text_color: iconColor });
                break;
            case 'popUpBg':
                await this.makeChange({ background_color: iconColor });
                break;
            case 'ctaText':
                await this.makeChange({ cta_text_color: iconColor });
                break;
            case 'ctaBg':
                await this.makeChange({ cta_background_color: iconColor });
                break;
        }
    }

    async onChangePopUpHeader(e: any) {
        await this.makeChange({ pop_up_header: e.detail.value });
    }

    async onChangePopUpDescription(e: any) {
        await this.makeChange({ pop_up_description: e.detail.value });
    }

    async onChangeCtaButtonText(e: any) {
        await this.makeChange({ cta_button_text: e.detail.value });
    }

    async onChangeOptInLanguage(e: any) {
        await this.makeChange({ opt_in_language: e.detail.value });
    }

    render() {
        return html`
            <div  id="${detectMobileViewport() ? '' : 'container'}">
                <div class="tab__content">
                        ${this.popUpSettings.map((setting) => {
                            return html `
                                <setting-component
                                    style="width:${setting.widthPercentage ?? 100}%"
                                    .setting_details=${setting}
                                    .is_hidden=${setting.isHidden ?? false}
                                    @open-upgrade-dialog=${this.openUpgradeDrawerEmailCollection}
                                    @on-change-value=${this.hasChange}
                                    @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.popUpSettings)}
                                    >
                                </setting-component>`;})}
                        </div>
                    </div>
                </div>
            <error-toast id="errorToast"></error-toast>

		`;
    }

    static get styles() {
        return css`
            #container {
                flex-direction: column;
                max-height: calc(100vh - 162px);
                overflow: hidden auto;
                margin-bottom: 1rem;
                padding-right: 20px;
            }

            /* width */
            #container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            #container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            #container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .form-input {
                width: 100%;
                margin: 14px 0;
            }

            .gatedField {
                display: block;
                cursor: pointer;
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                margin-bottom: 0px;
                color: var(--theme-input-color);
            }

            .advanced-settings__tabs {
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 20px;
                border: 1px solid var(--main-primary-color);
                margin-bottom: 1rem;
                font-size: 12px;
                margin-top: 1rem;
                font-size: 0.9rem;
                --mdc-icon-size: 12px;
            }

            .advanced-settings__tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
                line-height: 150%;
                color: var(--main-primary-color);
                background: transparent;
                padding: 5px 10px;
                cursor: pointer;
            }

            .advanced-settings__tab.right {
                border-radius: 0px 20px 20px 0px;
            }

            .advanced-settings__tab.left {
                border-radius: 20px 0px 0px 20px;
            }

            .advanced-settings__tab.active {
                background: var(--main-primary-color);
                color: var(--theme-white-color);
            }

            .tab-icon {
                width: 12px;
                height: 12px;
                margin-right: 5px;
            }

            .tab-icon.small {
                width: 12px;
                height: 12px;
            }

            .tab__content {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
            }

            .addon__footer {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                align-items: center;
                padding: 0 20px;
                gap: 10px;
            }

		    .input {
                width: 100%;
                padding: auto 5px;
                box-sizing: border-box;
            }

            .color-picker {
                height: 0;
                width: 0;
                opacity: 0;
                margin: 0px;
            }

            .color-selector-wrapper {
                display: flex;
                flex-direction: row;
                gap: 10px;
                justify-content: flex-start;
                margin-top: 10px;
            }

            .color-selector {
                width: 20%;
                display: flex;
                flex-direction: column;
                text-align: center;
                align-items: center;
                justify-content: space-between;
                border: 1px solid var(--theme-lighter-text-color);
                border-radius: 5px;
                padding: 5px;
                font-size: .75rem;
            }

            .color-wrapper {
                display: flex;
                align-items: center;
            }

            .color-preview {
                height: 20px;
                width: 20px;
                border-radius: 50%;
            }

            .email-collection--disabled {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
                color: var(--theme-lighter-text-color);
                width: 100%;
            }

            .email-collection {
                width: 100%;
            }

            .menuPng {
                margin-right: 0.25rem;
                height: 20px;
                margin-top: 20px;
            }

            .setting-switch {
                align-items: center;
                display: flex;
                gap: 8px;
                width: 100%;
                justify-content: center;
            }

            .addon__save {
                --mdc-theme-primary: var(--theme-border-color);
                --mdc-typography-button-text-transform: case;
                margin: 10px 0;
            }

            .addon__save--changed {
                --mdc-theme-primary: var(--main-success-color);
            }

            .addon__preview {
                --mdc-theme-primary: var(--main-primary-color);
                --mdc-button-outline-color: var(--main-primary-color);
                --mdc-typography-button-text-transform: case;
                margin: 10px 0;
                cursor: pointer;
            }

            .display-number {
                display: flex;
                align-items: center;
                gap: 14px;
            }

            .time-delayed {
                width: 60px;
            }

            .download-link {
                font-weight: 700;
                text-decoration: underline;
                cursor: pointer;
            }
        `;
    }
}